import React, { useEffect, useMemo, useState } from 'react';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import StatusBanner from '../../components/ui/StatusBanner';
import {
  EmployeeDashboardGrid,
  EmployeeDetailList,
  EmployeeSummaryCard,
  EmployeeWorkspaceEmptyState,
  EmployeeWorkspaceSection
} from '../../components/workforce/EmployeeWorkspace';
import { loadOrganization } from '../../services/workforceOrganizationApi';
import { CERTIFICATION_STATUS, classifyCertification } from './certificationStatus';

const EMPTY_WORKFORCE = Object.freeze({ status: 'idle', items: [], error: '' });

function resolvedLocationId(profile) {
  const value = Number(profile?.managerLocationId ?? profile?.locationId);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function displayName(profile) {
  return profile?.preferredName
    || [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim()
    || [profile?.managerFirstName, profile?.managerLastName].filter(Boolean).join(' ').trim()
    || `Employee #${profile?.id || '—'}`;
}

export function isActiveWorkforceRecord(profile) {
  return profile?.status === 'active' && !profile?.archivedAt;
}

export default function EmployeeWorkforcePlanningWorkspace({
  profile,
  documents = [],
  canReadDocuments = false,
  documentsEnabled = true,
  accessibleLocations = [],
  locationsLoading = false,
  now = Date.now()
}) {
  const profileId = Number(profile?.id);
  const companyId = Number(profile?.companyId);
  const hasProfile = profileId > 0 && companyId > 0;
  const [workforce, setWorkforce] = useState(hasProfile ? { ...EMPTY_WORKFORCE, status: 'loading' } : EMPTY_WORKFORCE);

  const locationNames = useMemo(
    () => new Map(accessibleLocations.map(location => [Number(location.id), location.code || location.name || `Location #${location.id}`])),
    [accessibleLocations]
  );

  useEffect(() => {
    if (!hasProfile) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    setWorkforce({ ...EMPTY_WORKFORCE, status: 'loading' });
    loadOrganization({ rootProfileId: profileId, depth: 1, status: 'any', signal: controller.signal })
      .then(items => {
        if (cancelled) return;
        setWorkforce({ status: 'loaded', items, error: '' });
      })
      .catch(error => {
        if (!cancelled) setWorkforce({ status: 'error', items: [], error: error?.message || 'Unable to load workforce records.' });
      });
    return () => { cancelled = true; controller.abort(); };
  }, [companyId, hasProfile, profileId]);

  const active = workforce.items.filter(isActiveWorkforceRecord);
  const inactive = workforce.items.filter(item => !isActiveWorkforceRecord(item));
  const unlocated = active.filter(item => resolvedLocationId(item) === null);
  const departments = [...new Set(active.map(item => item.department).filter(Boolean))];
  const roles = [...new Set(active.map(item => item.jobTitle || item.role).filter(Boolean))];
  const locations = [...new Set(active.map(item => item.locationCode || item.locationName || locationNames.get(resolvedLocationId(item))).filter(Boolean))];

  const certificationRecords = useMemo(() => (
    documentsEnabled && canReadDocuments
      ? documents
        .filter(document => document.documentType === 'certification'
          && Number(document.employeeProfileId) === profileId)
        .map(document => ({ ...document, certificationStatus: classifyCertification(document, now) }))
      : []
  ), [canReadDocuments, documents, documentsEnabled, now, profileId]);
  const expiring = certificationRecords.filter(item => item.certificationStatus === CERTIFICATION_STATUS.EXPIRING).length;
  const expired = certificationRecords.filter(item => item.certificationStatus === CERTIFICATION_STATUS.EXPIRED).length;

  return (
    <div className="employee-workforce-planning" data-testid="employee-workforce-planning-workspace">
      <EmployeeWorkspaceSection title="Workforce summary" description="Current authorized direct-report records">
        <EmployeeDashboardGrid columns="wide">
          <EmployeeSummaryCard label="Assigned employees" value={workforce.status === 'loaded' ? String(workforce.items.length) : '—'} detail="Current, non-archived employee profiles reporting to this leader" />
          <EmployeeSummaryCard label="Active employees" value={workforce.status === 'loaded' ? String(active.length) : '—'} detail="Status is exactly active in the employee profile model" />
          <EmployeeSummaryCard label="Inactive employees" value={workforce.status === 'loaded' ? String(inactive.length) : '—'} detail="On leave, suspended, or terminated; history remains preserved" />
          <EmployeeSummaryCard label="Location not recorded" value={workforce.status === 'loaded' ? String(unlocated.length) : '—'} detail="Kept visible rather than silently excluded" />
        </EmployeeDashboardGrid>
        <p className="employee-workspace__scope-note">Counts include only authorized direct-report records in this tenant. They are not a headcount target, staffing gap, vacancy count, capacity measure, or readiness score.</p>
      </EmployeeWorkspaceSection>

      <EmployeeWorkspaceSection title="Current workforce" description="Authorized employee-profile facts">
        {workforce.status === 'loading' || locationsLoading ? <LoadingSkeleton variant="paragraph" lines={4} /> : null}
        {workforce.status === 'error' ? <StatusBanner kind="error" message={workforce.error} /> : null}
        {workforce.status === 'loaded' && workforce.items.length === 0 ? <EmployeeWorkspaceEmptyState title="No assigned employees recorded" description="No authorized employee profile currently reports to this leader." release="" /> : null}
        {workforce.status === 'loaded' && workforce.items.length ? (
          <table className="employee-workspace__table">
            <caption>Current authorized workforce records</caption>
            <thead><tr><th scope="col">Employee</th><th scope="col">Status</th><th scope="col">Location</th><th scope="col">Department</th><th scope="col">Role / title</th></tr></thead>
            <tbody>{workforce.items.map(item => (
              <tr key={item.id}>
                <th scope="row">{displayName(item)}</th>
                <td>{item.status || 'Not recorded'}</td>
                <td>{item.locationCode || item.locationName || locationNames.get(resolvedLocationId(item)) || 'Location not recorded'}</td>
                <td>{item.department || 'Not recorded'}</td>
                <td>{item.jobTitle || item.role || 'Not recorded'}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : null}
      </EmployeeWorkspaceSection>

      <EmployeeDashboardGrid>
        <EmployeeWorkspaceSection title="Organizational coverage" description="Distribution of active authorized records">
          <EmployeeDetailList items={[
            { label: 'Locations represented', value: locations.join(', ') || 'Not recorded' },
            { label: 'Departments represented', value: departments.join(', ') || 'Not recorded' },
            { label: 'Roles / titles represented', value: roles.join(', ') || 'Not recorded' }
          ]} />
          <p className="employee-workspace__scope-note">No ideal span or coverage percentage is calculated.</p>
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Qualification records" description="Selected employee evidence only">
          {!documentsEnabled ? <EmployeeWorkspaceEmptyState title="Qualification records unavailable" description="The Documents module is disabled for this tenant." release="" />
            : !canReadDocuments ? <EmployeeWorkspaceEmptyState title="Qualification access restricted" description="Certification records require employee document permission." release="" />
              : <EmployeeDetailList items={[
                { label: 'Certification records available', value: String(certificationRecords.length) },
                { label: 'Expiring', value: String(expiring) },
                { label: 'Expired', value: String(expired) },
                { label: 'Role requirement status', value: 'Requirements not modeled' }
              ]} />}
          <p className="employee-workspace__scope-note">A certification record is evidence held by the selected employee; it does not prove a role requirement is satisfied.</p>
        </EmployeeWorkspaceSection>
      </EmployeeDashboardGrid>

      <EmployeeDashboardGrid>
        <EmployeeWorkspaceSection title="Workforce capacity" description="Current model availability">
          <EmployeeWorkspaceEmptyState title="Workforce capacity is not modeled" description="The current data model does not connect FTE, scheduled hours, actual hours, or demand to employee workforce capacity." release="Planned" />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Staffing demand and gaps" description="Current model availability">
          <EmployeeWorkspaceEmptyState title="Workforce demand is not modeled" description="Workforce demand and staffing-gap calculations are not modeled in the current data model." release="Planned" />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Vacancies and open positions" description="Current model availability">
          <EmployeeWorkspaceEmptyState title="Vacancies are not modeled" description="Position, vacancy, requisition, and hiring-pipeline records are not modeled in the current data model." release="Planned" />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Workforce movement" description="Current model availability">
          <EmployeeWorkspaceEmptyState title="Workforce movement history is not modeled" description="Effective-dated hire, transfer, promotion, and termination history is not available for workforce planning." release="Planned" />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Succession and readiness" description="Evidence availability, not employee scoring">
          <EmployeeWorkspaceEmptyState title="Succession readiness is not modeled" description="No promotion readiness, successor ranking, bench strength, leadership potential, or flight risk is generated." release="Planned" />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Workforce planning history" description="Current model availability">
          <EmployeeWorkspaceEmptyState title="Planning history is not modeled" description="The repository has no authoritative historical workforce plans or staffing scenarios." release="Planned" />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Operational data boundary" description="Manager and location context only">
          <EmployeeWorkspaceEmptyState title="Operational KPIs are not workforce readiness" description="Existing operational metrics are manager or location context and are not converted into employee capability, capacity, or workforce readiness." release="Not modeled" />
        </EmployeeWorkspaceSection>
      </EmployeeDashboardGrid>

      <EmployeeWorkspaceSection title="Future AI workforce planning" description="Placeholder only — no AI processing is active">
        <EmployeeWorkspaceEmptyState title="AI workforce planning is planned" description="No forecast, risk score, hiring recommendation, promotion recommendation, or automated workforce action is generated." release="Future release" />
      </EmployeeWorkspaceSection>
    </div>
  );
}
